/**
 * Prioritization Task Store
 * 
 * Manages prioritization tasks within cycles:
 * - CRUD operations for tasks
 * - Response collection and tracking
 * - Task lifecycle management (draft → open → closed → completed)
 * - Results aggregation
 * 
 * ============================================================================
 * STATE MACHINE INVARIANTS (Doc 02, Doc 03)
 * ============================================================================
 * 
 * INVARIANT 1: Tasks reference exactly one session.
 *   - Every task MUST have a valid sessionId
 *   - Tasks without sessions are invalid and non-actionable
 *   - A session can exist without a task, but not vice-versa
 * 
 * INVARIANT 2: Sessions own execution; tasks never substitute.
 *   - Tasks are responsibility assignments, not execution containers
 *   - The actual prioritization work happens in the Session
 *   - Task completion is derived from Session state
 *   - HISTORICAL NOTE: Tasks previously appeared to "do work" before Gap #5 fix
 * 
 * INVARIANT 3: Tasks do not advance cycle phases.
 *   - Task completion does not trigger phase transitions
 *   - Only the Cycle state machine controls phases
 */

import { create } from 'zustand';
import { getTaskPrimaryHref } from '../../lib/taskNavigation';
// Removed persist - Supabase is now the primary data store
import { v4 as uuidv4 } from 'uuid';
import type {
  PrioritizationTask,
  TaskStatus,
  TaskMode,
  TaskCategory,
  TaskResponse,
  TaskResponseData,
  AggregatedResult,
  PrioritizationToolId,
  TaskToolConfig,
  ValueEntryConfig,
} from '../../types/prioritization/prioritizationTask';
import { sendNotification, DEFAULT_EVENT_CHANNELS } from '../../lib/notificationService';
import { sendTaskAssignmentEmail } from '../../lib/emailService';
import { getIntegrationsApi } from '../../lib/integrationsApi';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
} from '../../lib/supabaseSync';
import type { Json } from '../../types/database.generated';
import { dispatchPrioritizationDashboardRefresh } from '../../lib/prioritizationDashboardRefreshEvents';
// CyclePrerequisite type deprecated - now part of unified task model

// ============================================================================
// Supabase Sync Helpers
// ============================================================================

const TABLE_NAME = 'prioritization_tasks';

/**
 * Some task categories omit session_id by design (e.g. Catchball proposal tasks
 * deep-link to the loop workbench via `description` instead of a tool session).
 */
function categoryAllowsMissingSessionId(category: TaskCategory | undefined): boolean {
  return category === 'catchball_proposal';
}

/**
 * Convert a PrioritizationTask to database format.
 *
 * CANONICAL MODEL: session-backed prioritization tasks should have sessionId.
 */
function taskToDbFormat(task: PrioritizationTask): Record<string, unknown> {
  const cat = task.category || 'prioritization';
  if (!task.sessionId && !categoryAllowsMissingSessionId(cat)) {
    console.warn(
      `[TaskStore] Converting task ${task.id} to DB format without session_id (category: ${cat}). ` +
        `Prioritization-style tasks should reference a session per Doc 02.`
    );
  }
  
  return {
    id: task.id,
    cycle_id: task.cycleId && task.cycleId !== 'standalone' ? task.cycleId : null,
    name: task.name,
    description: task.description,
    intent: task.intent,
    
    // Task category
    category: task.category || 'prioritization',
    
    // Value entry specific fields
    attribute_id: task.attributeId,
    attribute_name: task.attributeName,
    value_entry_config: task.valueEntryConfig,
    
    // Prioritization specific fields (deprecated - should be on Session)
    tool_id: task.toolId,
    tool_config: task.toolConfig || {},
    item_ids: task.itemIds,
    mode: task.mode,
    status: task.status,
    opens_at: task.opensAt?.toISOString(),
    closes_at: task.closesAt?.toISOString(),
    scheduled_at: task.scheduledAt?.toISOString(),
    duration: task.duration,
    facilitator_id: task.facilitatorId,
    participant_ids: task.participantIds,
    allow_view_others: task.allowViewOthers,
    allow_changes: task.allowChanges,
    responses: task.responses,
    aggregated_result: task.aggregatedResult,
    session_id: task.sessionId || null,
    created_by: task.createdBy,
    created_at: task.createdAt.toISOString(),
    updated_at: task.updatedAt.toISOString(),
    workspace_id: getCurrentWorkspaceId(),
  };
}

/**
 * Convert database record to PrioritizationTask.
 *
 * Session-backed tasks use `sessionId`; `catchball_proposal` tasks omit it and use
 * `description` for the workbench path. Other categories without `session_id` log a warning.
 */
function dbToTask(row: Record<string, unknown>): PrioritizationTask {
  const sessionId = row.session_id as string | undefined;
  const category = (row.category as TaskCategory) || 'prioritization';

  if (!sessionId && !categoryAllowsMissingSessionId(category)) {
    console.warn(
      `[TaskStore] Task ${row.id} has no session_id (category: ${category}). ` +
        `If this task should open inside a prioritization session, assign session_id in the database or recreate the task.`
    );
  }

  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    name: row.name as string,
    description: row.description as string | undefined,
    intent: row.intent as string | undefined,

    // Task category - defaults to 'prioritization' for legacy records
    category,
    
    // Value entry specific fields
    attributeId: row.attribute_id as string | undefined,
    attributeName: row.attribute_name as string | undefined,
    valueEntryConfig: row.value_entry_config as ValueEntryConfig | undefined,
    
    // Prioritization specific fields (deprecated - should be on Session)
    toolId: row.tool_id as PrioritizationToolId,
    toolConfig: row.tool_config as TaskToolConfig | undefined,
    itemIds: (row.item_ids as string[]) || [],
    mode: row.mode as TaskMode,
    status: row.status as TaskStatus,
    opensAt: row.opens_at ? new Date(row.opens_at as string) : undefined,
    closesAt: row.closes_at ? new Date(row.closes_at as string) : undefined,
    scheduledAt: row.scheduled_at ? new Date(row.scheduled_at as string) : undefined,
    duration: row.duration as number | undefined,
    facilitatorId: row.facilitator_id as string,
    participantIds: (row.participant_ids as string[]) || [],
    allowViewOthers: row.allow_view_others as boolean,
    allowChanges: row.allow_changes as boolean,
    responses: (row.responses as TaskResponse[]) || [],
    aggregatedResult: row.aggregated_result as AggregatedResult | undefined,
    // Empty when legacy or catchball_proposal (no tool session)
    sessionId: sessionId || '',
    createdBy: row.created_by as string,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

/**
 * Saves in flight, by task id. A task's later writes (open, close, answers)
 * wait for its insert, so an update can never reach the database before the
 * row exists. A refused insert rejects here, and the screen that asked reads it
 * with whenTaskSaved.
 */
const pendingInserts = new Map<string, Promise<void>>();

/**
 * Make the task through create_session_task (migration 1053), the one way a
 * person's task is made: on a session, by someone who may ask. The row the
 * database wrote replaces the local copy, so the tool and defaults it chose are
 * the ones the app shows.
 */
function insertTaskToSupabase(task: PrioritizationTask): Promise<void> {
  const save = (async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const { error } = await supabase.rpc('create_session_task', {
      p_id: task.id,
      p_session_id: task.sessionId,
      p_participant_ids: task.participantIds,
      p_name: task.name,
      p_category: task.category || 'prioritization',
      p_mode: task.mode,
      p_status: task.status,
      p_intent: task.intent ?? undefined,
      p_description: task.description ?? undefined,
      p_tool_id: task.toolId || undefined,
      p_tool_config: (task.toolConfig ?? {}) as Json,
      p_item_ids: task.itemIds,
      p_opens_at: task.opensAt?.toISOString(),
      p_closes_at: task.closesAt?.toISOString(),
      p_scheduled_at: task.scheduledAt?.toISOString(),
      p_duration: task.duration,
      p_attribute_id: task.attributeId,
      p_attribute_name: task.attributeName,
      p_value_entry_config: task.valueEntryConfig as Json | undefined,
      p_allow_view_others: task.allowViewOthers,
      p_allow_changes: task.allowChanges,
    });
    if (error) {
      console.error('[TaskStore] create_session_task refused:', error.message);
      usePrioritizationTaskStore.setState(state => ({
        tasks: state.tasks.filter(t => t.id !== task.id),
      }));
      throw new Error(error.message);
    }
    const { data: row } = await supabase.from(TABLE_NAME).select('*').eq('id', task.id).maybeSingle();
    if (row) {
      const saved = dbToTask(row as Record<string, unknown>);
      usePrioritizationTaskStore.setState(state => ({
        tasks: state.tasks.map(t => (t.id === task.id ? { ...saved, status: t.status, opensAt: t.opensAt ?? saved.opensAt } : t)),
      }));
    }
    dispatchPrioritizationDashboardRefresh({ source: 'prioritization_tasks.insert' });
  })();
  pendingInserts.set(task.id, save);
  save.catch(() => undefined).finally(() => {
    if (pendingInserts.get(task.id) === save) pendingInserts.delete(task.id);
  });
  return save;
}

/** Resolves when the task's insert is saved; rejects with the database's refusal. */
export function whenTaskSaved(taskId: string): Promise<void> {
  return pendingInserts.get(taskId) ?? Promise.resolve();
}

/**
 * Update task in Supabase
 */
async function updateTaskInSupabase(task: PrioritizationTask): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;

  try {
    // The row must exist before it can change (a refused insert leaves nothing to update).
    try {
      await whenTaskSaved(task.id);
    } catch {
      return;
    }
    const dbRecord = taskToDbFormat(task);
    const { error } = await supabase.from(TABLE_NAME).update(dbRecord).eq('id', task.id);
    if (error) {
      console.error('[TaskStore] Supabase update error:', error);
    } else {
      console.log('[TaskStore] Task updated in Supabase:', task.id);
      dispatchPrioritizationDashboardRefresh({ source: 'prioritization_tasks.update' });
    }
  } catch (err) {
    console.error('[TaskStore] Supabase update exception:', err);
  }
}

/**
 * Delete task from Supabase
 */
async function deleteTaskFromSupabase(taskId: string): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;

  try {
    try {
      await whenTaskSaved(taskId);
    } catch {
      return;
    }
    const { error } = await supabase.from(TABLE_NAME).delete().eq('id', taskId);
    if (error) {
      console.error('[TaskStore] Supabase delete error:', error);
    } else {
      console.log('[TaskStore] Task deleted from Supabase:', taskId);
      dispatchPrioritizationDashboardRefresh({ source: 'prioritization_tasks.delete' });
    }
  } catch (err) {
    console.error('[TaskStore] Supabase delete exception:', err);
  }
}

// ============================================================================
// Store Types
// ============================================================================

/**
 * Parameters for creating a new task.
 * 
 * CANONICAL MODEL (Doc 02, Doc 04):
 * - A Task represents a responsibility assignment, not an execution container
 * - A Task MUST reference exactly one Session (sessionId is REQUIRED)
 * - Tasks MUST NOT exist without a resolvable execution surface
 */
interface CreateTaskParams {
  /** Optional when creating a standalone task from the Tasks page. */
  cycleId?: string;
  name: string;
  description?: string;
  
  /**
   * Human-readable intent/purpose of this task.
   * Answers: "What am I expected to do?"
   */
  intent?: string;
  
  /** Task category - defaults to 'prioritization' */
  category?: TaskCategory;
  
  /** For value_entry tasks: which attribute */
  attributeId?: string;
  attributeName?: string;
  valueEntryConfig?: ValueEntryConfig;
  
  /** 
   * For prioritization tasks: which tool 
   * @deprecated Tool configuration should live on Session, not Task
   */
  toolId?: PrioritizationToolId;
  /** @deprecated Tool configuration should live on Session, not Task */
  toolConfig?: TaskToolConfig;
  
  /** @deprecated Item selection should live on Session, not Task */
  itemIds?: string[];
  mode?: TaskMode;
  opensAt?: Date;
  closesAt?: Date;
  scheduledAt?: Date;
  duration?: number;
  facilitatorId: string;
  participantIds: string[];
  allowViewOthers?: boolean;
  allowChanges?: boolean;
  createdBy: string;
  
  /**
   * Optional link to the session where work happens.
   * When provided, the task is actionable within that session.
   * Standalone tasks created from the Tasks page may omit this.
   */
  sessionId?: string;

  /**
   * When true, the task is created directly in 'open' status rather than 'draft'.
   * Use this when the creator intends the task to be immediately active
   * (e.g. standalone task creation from the Tasks page).
   */
  autoOpen?: boolean;
}

interface UpdateTaskParams {
  name?: string;
  description?: string;
  toolConfig?: TaskToolConfig;
  itemIds?: string[];
  mode?: TaskMode;
  opensAt?: Date;
  closesAt?: Date;
  scheduledAt?: Date;
  duration?: number;
  participantIds?: string[];
  allowViewOthers?: boolean;
  allowChanges?: boolean;
}

interface SubmitResponseParams {
  taskId: string;
  participantId: string;
  participantName: string;
  data: TaskResponseData;
  notes?: string;
}

interface PrioritizationTaskStore {
  tasks: PrioritizationTask[];
  
  // ========================================
  // Supabase Sync
  // ========================================
  
  /** Loading state for Supabase sync */
  isLoading: boolean;
  syncError: string | null;
  
  /** Load tasks from Supabase */
  loadTasksFromSupabase: () => Promise<void>;
  
  /** Load tasks for a specific cycle from Supabase */
  loadTasksForCycleFromSupabase: (cycleId: string) => Promise<void>;
  
  /** 
   * Fetch a single task from Supabase and return with expiration computed.
   * Use this for deep-linking to ensure fresh DB truth (per D-001).
   */
  fetchTaskWithExpiration: (taskId: string) => Promise<(PrioritizationTask & {
    isExpired: boolean;
    effectiveStatus: TaskStatus | 'expired';
  }) | null>;
  
  // ========================================
  // CRUD Operations
  // ========================================
  
  /** Create a new task */
  createTask: (params: CreateTaskParams) => PrioritizationTask;
  
  /** Update an existing task (only if in draft status) */
  updateTask: (taskId: string, updates: UpdateTaskParams) => void;
  
  /** Delete a task (only if in draft status) */
  deleteTask: (taskId: string) => void;
  
  /** Get a task by ID */
  getTask: (taskId: string) => PrioritizationTask | undefined;
  
  /** Get a task with expiration info computed */
  getTaskWithExpiration: (taskId: string) => (PrioritizationTask & {
    isExpired: boolean;
    effectiveStatus: TaskStatus | 'expired';
  }) | undefined;
  
  /** Check if a task is expired based on closesAt */
  isTaskExpired: (taskId: string) => boolean;
  
  /** Get all expired tasks that are still marked as 'open' */
  getExpiredOpenTasks: () => PrioritizationTask[];
  
  // ========================================
  // Task Lifecycle
  // ========================================
  
  /** 
   * Open a task for responses.
   * Optionally validates cycle prerequisites before opening.
   * Returns success status and error details if prerequisites not met.
   */
  openTask: (
    taskId: string,
    options?: { skipNotification?: boolean },
  ) => {
    success: boolean;
    error?: string;
  } | void;
  
  /** Close a task (no more responses accepted) */
  closeTask: (taskId: string) => void;
  
  /** Mark a task as completed (results finalized) */
  completeTask: (taskId: string) => void;
  
  /** Reopen a closed task (before completion) */
  reopenTask: (taskId: string) => void;
  
  // ========================================
  // Responses
  // ========================================
  
  /** Submit a participant's response */
  submitResponse: (params: SubmitResponseParams) => void;
  
  /** Update a participant's response (if allowed) */
  updateResponse: (taskId: string, participantId: string, data: TaskResponseData) => void;
  
  /** Remove a response (admin only) */
  removeResponse: (taskId: string, responseId: string) => void;
  
  // ========================================
  // Queries
  // ========================================
  
  /** Get all tasks for a cycle */
  getTasksForCycle: (cycleId: string) => PrioritizationTask[];
  
  /** Get all tasks where user is a participant */
  getTasksForParticipant: (userId: string) => PrioritizationTask[];
  
  /** Get pending tasks for a participant (open + not completed by them) */
  getPendingTasksForParticipant: (userId: string) => PrioritizationTask[];
  
  /** Get completed tasks for a participant */
  getCompletedTasksForParticipant: (userId: string) => PrioritizationTask[];
}

// ============================================================================
// Aggregation Helpers
// ============================================================================

function aggregateStackRanking(responses: TaskResponse[]): AggregatedResult['data'] {
  const rankSums: Record<string, number[]> = {};
  
  for (const response of responses) {
    if (response.data.type !== 'stack-ranking') continue;
    
    response.data.ranking.forEach((itemId, index) => {
      if (!rankSums[itemId]) rankSums[itemId] = [];
      rankSums[itemId].push(index + 1); // 1-indexed rank
    });
  }
  
  const averageRanks: Record<string, number> = {};
  const rankVariance: Record<string, number> = {};
  
  for (const [itemId, ranks] of Object.entries(rankSums)) {
    const avg = ranks.reduce((a, b) => a + b, 0) / ranks.length;
    averageRanks[itemId] = avg;
    
    // Calculate variance
    const variance = ranks.reduce((sum, r) => sum + Math.pow(r - avg, 2), 0) / ranks.length;
    rankVariance[itemId] = Math.sqrt(variance);
  }
  
  return { type: 'ranking', averageRanks, rankVariance };
}

function aggregateDotVoting(responses: TaskResponse[]): AggregatedResult['data'] {
  const totalVotes: Record<string, number> = {};
  let maxPossibleVotes = 0;
  
  for (const response of responses) {
    if (response.data.type !== 'dot-voting') continue;
    
    for (const [itemId, votes] of Object.entries(response.data.votes)) {
      totalVotes[itemId] = (totalVotes[itemId] || 0) + votes;
      maxPossibleVotes += votes;
    }
  }
  
  const votePercentage: Record<string, number> = {};
  for (const [itemId, votes] of Object.entries(totalVotes)) {
    votePercentage[itemId] = maxPossibleVotes > 0 
      ? Math.round((votes / maxPossibleVotes) * 100) 
      : 0;
  }
  
  return { type: 'votes', totalVotes, votePercentage };
}

function computeAggregatedResult(task: PrioritizationTask): AggregatedResult | undefined {
  if (task.responses.length === 0) return undefined;
  
  let data: AggregatedResult['data'];
  let finalRanking: string[] | undefined;
  
  switch (task.toolId) {
    case 'stack-ranking':
      data = aggregateStackRanking(task.responses);
      if (data.type === 'ranking') {
        // Sort by average rank ascending
        finalRanking = Object.entries(data.averageRanks)
          .sort(([, a], [, b]) => a - b)
          .map(([id]) => id);
      }
      break;
      
    case 'dot-voting':
      data = aggregateDotVoting(task.responses);
      if (data.type === 'votes') {
        // Sort by total votes descending
        finalRanking = Object.entries(data.totalVotes)
          .sort(([, a], [, b]) => b - a)
          .map(([id]) => id);
      }
      break;
      
    default:
      // Default to simple ranking aggregation
      data = aggregateStackRanking(task.responses);
      break;
  }
  
  // Calculate agreement score (inverse of average variance)
  let agreementScore = 1;
  if (data.type === 'ranking' && Object.keys(data.rankVariance).length > 0) {
    const avgVariance = Object.values(data.rankVariance).reduce((a, b) => a + b, 0) 
      / Object.keys(data.rankVariance).length;
    agreementScore = Math.max(0, 1 - (avgVariance / task.itemIds.length));
  }
  
  return {
    taskId: task.id,
    computedAt: new Date(),
    responseCount: task.responses.length,
    data,
    finalRanking,
    agreementScore: Math.round(agreementScore * 100) / 100,
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const usePrioritizationTaskStore = create<PrioritizationTaskStore>()((set, get) => ({
      tasks: [],
      isLoading: false,
      syncError: null,
      
      // ========================================
      // Supabase Sync
      // ========================================
      
      loadTasksFromSupabase: async () => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          console.log('[TaskStore] Supabase not available, using localStorage');
          return;
        }
        
        set({ isLoading: true, syncError: null });
        
        try {
          let query = supabase.from(TABLE_NAME).select('*');
          
          // Always filter by the active workspace when one is selected — this keeps the
          // result set small (index seek vs full scan) and is especially important for
          // platform admins who bypass RLS and would otherwise read all workspaces' tasks.
          const workspaceId = getCurrentWorkspaceId();
          if (workspaceId) {
            query = query.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);
          }
          
          const { data, error } = await query.order('created_at', { ascending: false });
          
          if (error) {
            console.error('[TaskStore] Supabase load error:', error);
            set({ isLoading: false, syncError: error.message });
            return;
          }
          
          const tasks = (data || []).map(row => dbToTask(row as Record<string, unknown>));
          set({ tasks, isLoading: false, syncError: null });
          console.log(`[TaskStore] Loaded ${tasks.length} tasks from Supabase`);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          console.error('[TaskStore] Supabase load exception:', err);
          set({ isLoading: false, syncError: message });
        }
      },
      
      loadTasksForCycleFromSupabase: async (cycleId) => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        set({ isLoading: true, syncError: null });
        
        try {
          const workspaceId = getCurrentWorkspaceId();
          let query = supabase
            .from(TABLE_NAME)
            .select('*')
            .eq('cycle_id', cycleId);
          if (workspaceId) {
            query = query.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);
          }
          const { data, error } = await query.order('created_at', { ascending: false });
          
          if (error) {
            console.error('[TaskStore] Supabase load by cycle error:', error);
            set({ isLoading: false, syncError: error.message });
            return;
          }
          
          const loadedTasks = (data || []).map(row => dbToTask(row as Record<string, unknown>));
          
          // Merge with existing tasks (replace tasks for this cycle, keep others)
          set(state => {
            const otherTasks = state.tasks.filter(t => t.cycleId !== cycleId);
            return { 
              tasks: [...otherTasks, ...loadedTasks], 
              isLoading: false, 
              syncError: null 
            };
          });
          console.log(`[TaskStore] Loaded ${loadedTasks.length} tasks for cycle ${cycleId}`);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          console.error('[TaskStore] Supabase load by cycle exception:', err);
          set({ isLoading: false, syncError: message });
        }
      },
      
      /**
       * Fetch a single task from Supabase with fresh data and compute expiration.
       * Per D-001: DB is source of truth, this avoids stale client-side data.
       * Use this for deep-linking to ensure accurate expiry.
       */
      fetchTaskWithExpiration: async (taskId) => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          // Fall back to local cache if Supabase unavailable
          return get().getTaskWithExpiration(taskId) || null;
        }
        
        try {
          const { data, error } = await supabase
            .from(TABLE_NAME)
            .select('*')
            .eq('id', taskId)
            .single();
          
          if (error || !data) {
            console.warn('[TaskStore] Task not found in Supabase:', taskId);
            return null;
          }
          
          const task = dbToTask(data as Record<string, unknown>);
          
          // Update local cache with fresh data
          set(state => {
            const otherTasks = state.tasks.filter(t => t.id !== taskId);
            return { tasks: [...otherTasks, task] };
          });
          
          // Compute expiration from DB truth
          const now = Date.now();
          const closesAtTime = task.closesAt ? new Date(task.closesAt).getTime() : null;
          const isExpired = closesAtTime !== null && closesAtTime < now;
          const effectiveStatus = isExpired && task.status === 'open' ? 'expired' as const : task.status;
          
          console.log(`[TaskStore] Fetched task ${taskId} from DB: status=${task.status}, expired=${isExpired}`);
          
          return {
            ...task,
            isExpired,
            effectiveStatus,
          };
        } catch (err) {
          console.error('[TaskStore] Failed to fetch task:', err);
          // Fall back to local cache
          return get().getTaskWithExpiration(taskId) || null;
        }
      },
      
      // ========================================
      // CRUD Operations
      // ========================================
      
      /**
       * Create a new task.
       * 
       * INVARIANT 1: Tasks reference exactly one session (Doc 02, Doc 04).
       *   - A task without a sessionId is invalid and should not be created
       *   - A session can exist without a task, but not vice-versa
       * 
       * INVARIANT 2: Sessions own execution; tasks never substitute.
       *   - Tasks are responsibility assignments, NOT execution containers
       *   - The actual prioritization work happens in the Session
       *   - Task status is derived from Session state
       * 
       * HISTORICAL NOTE: Before Gap #5 fix, tasks could be created without
       * sessions. This is now canonically invalid per Doc 02.
       */
      createTask: (params) => {
        // Canon Doc 03 §2.8: a person's task is their part in exactly one
        // session. create_session_task refuses one without; say so here first.
        if (!params.sessionId) {
          throw new Error('A task is someone\'s part in a session. Choose the session it is for.');
        }
        // The session's own tool is used unless the caller names one
        // (create_session_task, migration 1053); there is no placeholder.
        const toolId = (params.toolId ?? '') as PrioritizationToolId;
        
        const now = new Date();

        const taskStatus: TaskStatus = params.autoOpen ? 'open' : 'draft';

        const task: PrioritizationTask = {
          id: uuidv4(),
          cycleId: params.cycleId,
          name: params.name,
          description: params.description,
          intent: params.intent || `Complete ${params.name}`,
          
          // Task category - defaults to 'prioritization' for backward compatibility
          category: params.category || 'prioritization',
          
          // Value entry specific fields
          attributeId: params.attributeId,
          attributeName: params.attributeName,
          valueEntryConfig: params.valueEntryConfig,
          
          // Prioritization specific fields (deprecated - should be on Session)
          toolId,
          toolConfig: params.toolConfig ?? {},
          
          itemIds: params.itemIds ?? [],
          status: taskStatus,
          mode: params.mode ?? 'async',
          opensAt: params.autoOpen ? (params.opensAt ?? now) : params.opensAt,
          closesAt: params.closesAt,
          scheduledAt: params.scheduledAt,
          duration: params.duration,
          facilitatorId: params.facilitatorId,
          participantIds: params.participantIds,
          allowViewOthers: params.allowViewOthers ?? false,
          allowChanges: params.allowChanges ?? true,
          responses: [],
          sessionId: params.sessionId ?? '',
          createdAt: now,
          createdBy: params.createdBy,
          updatedAt: now,
        };

        set(state => ({ tasks: [...state.tasks, task] }));

        // Saved through create_session_task; whenTaskSaved(task.id) reports a refusal.
        void insertTaskToSupabase(task).catch(() => undefined);

        return task;
      },
      
      updateTask: (taskId, updates) => {
        let updatedTask: PrioritizationTask | undefined;
        set(state => ({
          tasks: state.tasks.map(task => {
            if (task.id !== taskId) return task;
            if (task.status !== 'draft') {
              console.warn('[TaskStore] Cannot update task that is not in draft status');
              return task;
            }
            updatedTask = { ...task, ...updates, updatedAt: new Date() };
            return updatedTask;
          }),
        }));
        
        // Sync to Supabase
        if (updatedTask) {
          updateTaskInSupabase(updatedTask);
        }
      },
      
      deleteTask: (taskId) => {
        const task = get().tasks.find(t => t.id === taskId);
        if (task && task.status !== 'draft') {
          console.warn('[TaskStore] Cannot delete task that is not in draft status');
          return;
        }
        set(state => ({ tasks: state.tasks.filter(t => t.id !== taskId) }));
        
        // Sync to Supabase
        deleteTaskFromSupabase(taskId);
      },
      
      getTask: (taskId) => {
        return get().tasks.find(t => t.id === taskId);
      },
      
      /**
       * Get task with expiration info computed.
       * IMPORTANT: Per D-001, this computes expiry from the task's closesAt 
       * (which is DB truth when fetched from Supabase).
       * For critical operations, call loadTasksFromSupabase first to ensure fresh data.
       */
      getTaskWithExpiration: (taskId) => {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return undefined;
        
        // Use Date.now() for consistency - closesAt is from DB (source of truth)
        const now = Date.now();
        const closesAtTime = task.closesAt ? new Date(task.closesAt).getTime() : null;
        const isExpired = closesAtTime !== null && closesAtTime < now;
        const effectiveStatus = isExpired && task.status === 'open' ? 'expired' : task.status;
        
        return {
          ...task,
          isExpired,
          effectiveStatus,
        };
      },
      
      /**
       * Check if task is expired based on closesAt from DB.
       * For deep-linking use cases, prefer getTaskWithExpirationFromDb which re-fetches.
       */
      isTaskExpired: (taskId) => {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task || !task.closesAt) return false;
        return new Date(task.closesAt).getTime() < Date.now();
      },
      
      getExpiredOpenTasks: () => {
        const now = Date.now();
        return get().tasks.filter(t => 
          t.status === 'open' && 
          t.closesAt && 
          new Date(t.closesAt).getTime() < now
        );
      },
      
      // ========================================
      // Task Lifecycle
      // ========================================
      
      openTask: (taskId, options) => {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) {
          console.warn('[TaskStore] Task not found:', taskId);
          return { success: false, error: 'Task not found' };
        }

        if (task.status !== 'draft') {
          console.warn('[TaskStore] Can only open tasks in draft status');
          return { success: false, error: 'Task is not in draft status' };
        }

        // Update task status
        set(state => ({
          tasks: state.tasks.map(t => {
            if (t.id !== taskId) return t;
            return { 
              ...t, 
              status: 'open' as TaskStatus,
              opensAt: t.opensAt || new Date(),
              updatedAt: new Date(),
            };
          }),
        }));

        // GAP-4: live go-live already sent session_started via notifySessionLiveNow —
        // skip duplicate task_assigned bells for sync "Join live session" tasks.
        if (options?.skipNotification) {
          return { success: true };
        }
        
        // Send notifications to all participants
        const notifyParticipants = async () => {
          const channels = DEFAULT_EVENT_CHANNELS.task_assigned;
          
          try {
            // Send in-app notification
            await sendNotification({
              event: 'task_assigned',
              channels,
              recipientIds: task.participantIds,
              title: 'New Prioritization Task',
              body: `You have been assigned to "${task.name}". Please complete your prioritization by ${task.closesAt ? new Date(task.closesAt).toLocaleDateString() : 'the deadline'}.`,
              priority: 'normal',
              data: {
                taskId: task.id,
                taskName: task.name,
                toolId: task.toolId,
                ...(task.cycleId && task.cycleId !== 'standalone' ? { cycleId: task.cycleId } : {}),
                sessionId: task.sessionId,
                closesAt: task.closesAt,
              },
              actionUrl: getTaskPrimaryHref(task),
              actionLabel: 'Start Task',
              senderId: task.createdBy,
            });
            
            // Try to create Google Calendar invite first
            let calendarInviteSent = false;
            if (task.closesAt) {
              try {
                const api = getIntegrationsApi();
                const calendarResult = await api.createCalendarInvite({
                  taskId: task.id,
                  taskName: task.name,
                  toolName: task.toolId,
                  description: task.description || `Prioritization activity for ${task.name}`,
                  scheduledStart: task.opensAt || new Date(),
                  durationMinutes: 30,
                  participantUserIds: task.participantIds,
                });
                
                if (calendarResult.success) {
                  console.log(`[TaskStore] Created calendar event ${calendarResult.eventId} for task ${taskId}`);
                  calendarInviteSent = true;
                  
                  if (calendarResult.missingUsers && calendarResult.missingUsers.length > 0) {
                    console.warn(`[TaskStore] Could not invite these users (no email): ${calendarResult.missingUsers.join(', ')}`);
                  }
                } else {
                  console.warn(`[TaskStore] Calendar invite failed: ${calendarResult.error}`);
                }
              } catch (calendarError) {
                console.warn('[TaskStore] Google Calendar not available, falling back to email:', calendarError);
              }
            }
            
            // Send email notifications as fallback or in addition to calendar
            if (channels.includes('email') && !calendarInviteSent) {
              for (const participantId of task.participantIds) {
                try {
                  await sendTaskAssignmentEmail({
                    recipientId: participantId,
                    taskId: task.id,
                    taskName: task.name,
                    toolName: task.toolId,
                    deadline: task.closesAt,
                    cycleId: task.cycleId && task.cycleId !== 'standalone' ? task.cycleId : undefined,
                    facilitatorId: task.facilitatorId,
                  });
                } catch (emailError) {
                  console.error(`[TaskStore] Failed to send email to ${participantId}:`, emailError);
                }
              }
            }
            
            console.log(`[TaskStore] Sent notifications to ${task.participantIds.length} participants for task ${taskId} (calendar: ${calendarInviteSent})`);
          } catch (error) {
            console.error('[TaskStore] Failed to send task notifications:', error);
          }
        };
        
        // Fire and forget notifications (don't block on them)
        notifyParticipants();
        
        // Sync to Supabase
        const updatedTask = get().tasks.find(t => t.id === taskId);
        if (updatedTask) {
          updateTaskInSupabase(updatedTask);
        }
        
        return { success: true };
      },
      
      closeTask: (taskId) => {
        set(state => ({
          tasks: state.tasks.map(task => {
            if (task.id !== taskId) return task;
            if (task.status !== 'open') {
              console.warn('[TaskStore] Can only close tasks in open status');
              return task;
            }
            
            // Compute aggregated results when closing
            const aggregatedResult = computeAggregatedResult(task);
            
            return { 
              ...task, 
              status: 'closed' as TaskStatus,
              aggregatedResult,
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase
        const closedTask = get().tasks.find(t => t.id === taskId);
        if (closedTask) {
          updateTaskInSupabase(closedTask);
        }
      },
      
      completeTask: (taskId) => {
        set(state => ({
          tasks: state.tasks.map(task => {
            if (task.id !== taskId) return task;
            if (task.status !== 'closed') {
              console.warn('[TaskStore] Can only complete tasks in closed status');
              return task;
            }
            return { 
              ...task, 
              status: 'completed' as TaskStatus,
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase
        const completedTask = get().tasks.find(t => t.id === taskId);
        if (completedTask) {
          updateTaskInSupabase(completedTask);
        }
      },
      
      reopenTask: (taskId) => {
        set(state => ({
          tasks: state.tasks.map(task => {
            if (task.id !== taskId) return task;
            if (task.status !== 'closed') {
              console.warn('[TaskStore] Can only reopen tasks in closed status');
              return task;
            }
            return { 
              ...task, 
              status: 'open' as TaskStatus,
              aggregatedResult: undefined,
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase
        const reopenedTask = get().tasks.find(t => t.id === taskId);
        if (reopenedTask) {
          updateTaskInSupabase(reopenedTask);
        }
      },
      
      // ========================================
      // Responses
      // ========================================
      
      submitResponse: (params) => {
        const task = get().tasks.find(t => t.id === params.taskId);
        if (!task) {
          console.warn('[TaskStore] Task not found:', params.taskId);
          return;
        }
        
        if (task.status !== 'open') {
          console.warn('[TaskStore] Cannot submit response to task that is not open');
          return;
        }
        
        // Check if participant already submitted
        const existingResponse = task.responses.find(r => r.participantId === params.participantId);
        if (existingResponse && !task.allowChanges) {
          console.warn('[TaskStore] Participant already submitted and changes not allowed');
          return;
        }
        
        const response: TaskResponse = {
          id: uuidv4(),
          taskId: params.taskId,
          participantId: params.participantId,
          participantName: params.participantName,
          submittedAt: new Date(),
          data: params.data,
          notes: params.notes,
        };
        
        set(state => ({
          tasks: state.tasks.map(t => {
            if (t.id !== params.taskId) return t;
            
            // Replace existing response or add new one
            const filteredResponses = existingResponse
              ? t.responses.filter(r => r.participantId !== params.participantId)
              : t.responses;
            
            return {
              ...t,
              responses: [...filteredResponses, response],
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase
        const updatedTask = get().tasks.find(t => t.id === params.taskId);
        if (updatedTask) {
          updateTaskInSupabase(updatedTask);
        }
      },
      
      updateResponse: (taskId, participantId, data) => {
        set(state => ({
          tasks: state.tasks.map(task => {
            if (task.id !== taskId) return task;
            if (!task.allowChanges) {
              console.warn('[TaskStore] Changes not allowed for this task');
              return task;
            }
            
            return {
              ...task,
              responses: task.responses.map(r => {
                if (r.participantId !== participantId) return r;
                return { ...r, data, updatedAt: new Date() };
              }),
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase
        const updatedTask = get().tasks.find(t => t.id === taskId);
        if (updatedTask) {
          updateTaskInSupabase(updatedTask);
        }
      },
      
      removeResponse: (taskId, responseId) => {
        set(state => ({
          tasks: state.tasks.map(task => {
            if (task.id !== taskId) return task;
            return {
              ...task,
              responses: task.responses.filter(r => r.id !== responseId),
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase
        const updatedTask = get().tasks.find(t => t.id === taskId);
        if (updatedTask) {
          updateTaskInSupabase(updatedTask);
        }
      },
      
      // ========================================
      // Queries
      // ========================================
      
      getTasksForCycle: (cycleId) => {
        return get().tasks.filter(t => t.cycleId === cycleId);
      },
      
      getTasksForParticipant: (userId) => {
        return get().tasks.filter(t => t.participantIds.includes(userId));
      },
      
      getPendingTasksForParticipant: (userId) => {
        return get().tasks.filter(t => 
          t.participantIds.includes(userId) &&
          t.status === 'open' &&
          !t.responses.some(r => r.participantId === userId)
        );
      },
      
      getCompletedTasksForParticipant: (userId) => {
        return get().tasks.filter(t => 
          t.participantIds.includes(userId) &&
          t.responses.some(r => r.participantId === userId)
        );
      },
    }));

export default usePrioritizationTaskStore;
